import React, {
  FunctionComponent,
  Fragment,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Flex, Text, Button, Hyperlink } from 'mdlkit';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  EligibilityCheckResponse,
  AffiliationType,
  PatientSubmitError,
} from '../../../interfaces/register';
import { event } from '../../../lib/analytics';
import { REGISTRATION } from '../../../constants/events';
import { BrandingContext } from '../../app/BrandingProvider';
import getAffiliationBrandingAsync from '../../../api/branding/getAffiliationBrandingAsync';

interface Props {
  eligibilityCheck?: EligibilityCheckResponse;
  onContinue(
    affiliationType?: AffiliationType
  ): Promise<boolean | void | PatientSubmitError>;
  showContinueWithoutCoverageButton: boolean;
}

const IneligibleReason: FunctionComponent<Props> = ({
  eligibilityCheck,
  onContinue,
  showContinueWithoutCoverageButton,
}: Props) => {
  const { formatMessage: f } = useIntl();
  const { branding } = useContext(BrandingContext);
  const [localBranding, setLocalBranding] = useState(branding || null);

  const updateBrandingContent = async (): Promise<void> => {
    if (branding === undefined && eligibilityCheck?.affiliationId) {
      const { branding: fetchedBranding } = await getAffiliationBrandingAsync(
        `${eligibilityCheck?.affiliationId}`,
        'en',
        null
      );
      if (fetchedBranding) {
        setLocalBranding(fetchedBranding);
      }
    }
  };

  useEffect(() => {
    updateBrandingContent();
  }, []);

  return (
    <Flex column align="center">
      <Text pt="md" data-testid="eligibilityFailureReason">
        {eligibilityCheck && eligibilityCheck.reason
          ? eligibilityCheck.reason.replace(
              'and click on "Verify" again',
              'by clicking "Retry" and re-submit your information'
            )
          : f({ id: 'eligibilityError.notEligibleBody' })}
      </Text>

      {showContinueWithoutCoverageButton && eligibilityCheck ? (
        <Fragment>
          <Text pt="md">
            <FormattedMessage id="eligibilityError.continueWithoutCoverageBody" />
          </Text>
          <Button
            size="large"
            mt="md"
            data-testid="continueBtn"
            onClick={() => {
              const controlText = f({
                id: 'eligibilityError.continueWithoutCoverageButton',
              });
              window.createPageActionEvent({
                controlName: 'continue-without-coverage-button',
                controlText,
              });
              event({
                action: 'select_eligibility_check_fail_continue',
                category: REGISTRATION,
                parameters: { affiliationId: eligibilityCheck.affiliationId },
              });
              return onContinue(eligibilityCheck.affiliationType);
            }}
          >
            <FormattedMessage id="eligibilityError.continueWithoutCoverageButton" />
          </Button>
        </Fragment>
      ) : null}

      {localBranding?.default.external_phone_number_for_elegibility && (
        <Text pt="md">
          <FormattedMessage
            id="eligibilityError.callSupport"
            values={{
              phone: (
                <Hyperlink
                  href={`tel:${localBranding.default.external_phone_number_for_elegibility}`}
                >
                  {localBranding.default.external_phone_number_for_elegibility}
                </Hyperlink>
              ),
            }}
          />
        </Text>
      )}
      {!localBranding?.default.external_phone_number_for_elegibility && (
        <Text pt="md">
          <FormattedMessage
            id="eligibilityError.callSupport"
            values={{
              phone: (
                <Hyperlink href="tel:(888) 726-3171">(888) 726-3171</Hyperlink>
              ),
            }}
          />
        </Text>
      )}
    </Flex>
  );
};
export default IneligibleReason;
